import { all, call, delay, put, takeLatest } from '@redux-saga/core/effects';
import {
  addTapToTipTour,
  addToursToProfile,
  connectStripe,
  editTapToTipTour,
  fetchActiveTours,
  fetchAgentsProfiles,
  fetchReviewsStatistic,
  fetchTransactions,
  fetchUserProfileById,
  multipleAssign,
  revokeInvite,
  sendInvitesForGuides,
  updateProfile,
  uploadUserProfileImage
} from '../../api/taptotip';
import { deleteTour, fetchAvailableTours } from '../../api/tour';
import { currentUser } from '../../api/user';
import { setCurrentUser } from '../../store/user/actionCreators';
import { date2str } from '../../utils/helpers';
import { extractStatusBarError, snackbarStatus } from '../StatusBar';
import { setSnackbarStatus } from '../StatusBar/actions';
import * as actions from './actions';
import * as types from './types';

const mapUserProfile = ({ profile, tours, features }) => {
  return {
    ...profile,
    tours,
    defaultPaymentAmount: profile?.defaultPaymentAmount / 100 || 0,
    country: profile.country?.toLowerCase() || 'ie',
    features
  };
};

const mapProfileForUpdate = profile => ({
  ...profile,
  defaultPaymentAmount: profile.defaultPaymentAmount * 100
});

// The dashboard and top nav read the signed-in user (state.user), which is
// loaded once at app start, not this container's profile. After a save or a
// photo upload, reload it so the new name or photo shows there without a page
// refresh. A failed reload keeps the user we have: unlike the app-start fetch
// (fetchCurrentUserSaga), it must never sign the guide out.
export function* refreshSignedInUser() {
  try {
    const response = yield call(currentUser);
    const user = response?.data?.data;
    if (user?.userId) yield put(setCurrentUser(user));
  } catch (err) {
    // Keep the current user; the next page load fetches it again.
  }
}

function normalizeDateStrings(payload) {
  return {
    ...payload,
    dateRange: {
      start: date2str(payload.dateRange.start),
      end: date2str(payload.dateRange.end)
    }
  };
}

function* fetchTransactionsWorker({ payload = null }) {
  try {
    const transactions = yield call(fetchTransactions, payload);

    yield put(actions.fetchTransactionsSuccess(transactions));
  } catch (err) {
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(err.message)));
  }
}

function* fetchProfileWorker({ payload }) {
  try {
    const profile = yield call(fetchUserProfileById, payload);

    yield put(actions.fetchProfileSuccess(mapUserProfile(profile)));
  } catch (err) {
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(err.message)));
  }
}

// `quiet` skips the Saving/Saved toasts for callers that show their own
// progress (onboarding moves to the next step instead). Errors always show.
// `onSuccess` / `onError` let a caller that must not move on until the save
// lands (onboarding) wait for the outcome instead of guessing.
export function* updateProfileWorker({ payload: { userId, profile, quiet = false, onSuccess, onError } }) {
  try {
    if (!quiet) yield put(setSnackbarStatus(snackbarStatus.saving));
    const response = yield all([
      call(updateProfile, userId, mapProfileForUpdate(profile)),
      delay(1000)
    ]);

    yield put(
      actions.updateProfileSuccess({
        ...response[0],
        defaultPaymentAmount: response[0].defaultPaymentAmount / 100
      })
    );
    yield call(refreshSignedInUser);
    if (!quiet) yield put(setSnackbarStatus(snackbarStatus.success));
    if (onSuccess) yield call(onSuccess);
  } catch (error) {
    yield put(actions.facedError(error));
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(error.message)));
    if (onError) yield call(onError, error);
  }
}

export function* uploadUserProfileImageWorker({ payload }) {
  try {
    yield put(setSnackbarStatus(snackbarStatus.saving));

    const response = yield all([call(uploadUserProfileImage, payload), delay(1000)]);

    yield put(actions.uploadUserProfileImageSuccess(response[0].url));
    // The upload saves the photo to the profile server-side as well.
    yield call(refreshSignedInUser);
    yield put(setSnackbarStatus(snackbarStatus.success));
  } catch (err) {
    yield put(actions.facedError(err));
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(err.message)));
  }
}

function* connectStripeWorker() {
  try {
    const response = yield call(connectStripe);

    yield put(actions.connectStripeSuccess(response));
  } catch (err) {
    if (err.status !== 400) {
      yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(err.message)));
    }
  }
}

function* fetchAgentsProfilesWorker() {
  try {
    const agentsProfiles = yield call(fetchAgentsProfiles);

    yield put(actions.fetchAgentsProfilesSuccess(agentsProfiles));
  } catch (err) {
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(err.message)));
  }
}

function* fetchSendInvitesWorker({ payload }) {
  try {
    yield call(sendInvitesForGuides, payload);
    yield call(fetchAgentsProfilesWorker);
    yield put(setSnackbarStatus(snackbarStatus.success));
  } catch (err) {
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(err.message)));
  }
}

function* revokeInviteWorker({ payload }) {
  try {
    yield call(revokeInvite, payload);
    yield call(fetchAgentsProfilesWorker);
    yield put(setSnackbarStatus(snackbarStatus.inviteDeleted));
  } catch (err) {
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(err.message)));
  }
}

function* fetchActiveToursWorker() {
  try {
    const activeTours = yield call(fetchActiveTours);

    yield put(actions.fetchActiveToursSuccess(activeTours));
  } catch (err) {
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(err.message)));
  }
}

function* fetchToursWorker() {
  try {
    const toursResponse = yield call(fetchAvailableTours, 'active');
    yield put(actions.fetchToursSuccess(toursResponse.data.data.tours));
  } catch (error) {
    yield put(actions.facedError(error));
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(error.message)));
  }
}

function* addToursToProfileWorker({ payload: { userId, tourIds } }) {
  try {
    yield call(addToursToProfile, userId, tourIds);

    yield put(actions.fetchAgentsProfiles());
    yield put(actions.fetchProfile(userId));
  } catch (err) {
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(err.message)));
  }
}

function* addTapToTipTourWorker({ payload }) {
  try {
    yield call(addTapToTipTour, payload);
    yield put(setSnackbarStatus(snackbarStatus.success));
    yield put(actions.fetchActiveTours());
    yield fetchToursWorker();
  } catch (err) {
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(err.message)));
  }
}

function* editTapToTipTourWorker({ payload }) {
  try {
    yield call(editTapToTipTour, payload);
    yield put(setSnackbarStatus(snackbarStatus.success));
    yield put(actions.fetchActiveTours());
    yield fetchToursWorker();
  } catch (err) {
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(err.message)));
  }
}

function* fetchReviewsStatisticWorker({ payload }) {
  try {
    const result = yield call(fetchReviewsStatistic, normalizeDateStrings(payload));
    yield put(actions.fetchReviewsStatisticSuccess(result));
  } catch (err) {
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(err.message)));
  }
}


function* multipleAssignWorker({ payload: { tourIds, guideIds } }) {
  try {
    yield put(setSnackbarStatus(snackbarStatus.saving));
    yield call(multipleAssign, tourIds, guideIds);
    yield put(setSnackbarStatus(snackbarStatus.success));
    yield put(actions.fetchAgentsProfiles());
  } catch (err) {
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(err.message)));
  }
}

function* deleteTourWorker({ payload }) {
  try {
    yield call(deleteTour, payload);
    yield put(setSnackbarStatus(snackbarStatus.success));
    yield put(actions.fetchActiveTours());
    yield fetchToursWorker();
  } catch (error) {
    yield put(setSnackbarStatus(snackbarStatus.error, '', extractStatusBarError(error.details)));
  }
}

export default function* tapToTipWatcher() {
  yield takeLatest(types.FETCH_TRANSACTIONS, fetchTransactionsWorker);
  yield takeLatest(types.FETCH_PROFILE, fetchProfileWorker);
  yield takeLatest(types.UPDATE_PROFILE, updateProfileWorker);
  yield takeLatest(types.UPLOAD_USER_PROFILE_IMAGE, uploadUserProfileImageWorker);
  yield takeLatest(types.CONNECT_STRIPE, connectStripeWorker);
  yield takeLatest(types.FETCH_AGENTS_PROFILES, fetchAgentsProfilesWorker);
  yield takeLatest(types.SEND_GUIDE_INVITES, fetchSendInvitesWorker);
  yield takeLatest(types.FETCH_ACTIVE_TOURS, fetchActiveToursWorker);
  yield takeLatest(types.REVOKE_INVITE, revokeInviteWorker);
  yield takeLatest(types.ADD_TOURS_TO_PROFILE, addToursToProfileWorker);
  yield takeLatest(types.ADD_TTT_TOUR, addTapToTipTourWorker);
  yield takeLatest(types.EDIT_TTT_TOUR, editTapToTipTourWorker);
  yield takeLatest(types.FETCH_TOURS, fetchToursWorker);
  yield takeLatest(types.FETCH_REVIEWS_STATISTIC, fetchReviewsStatisticWorker);
  yield takeLatest(types.MULTIPLE_ASSIGN, multipleAssignWorker);
  yield takeLatest(types.DELETE_TOUR, deleteTourWorker);
}
